import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CANCELLATION_DATE_DELTA, tripBuilderAdditionalInfoValidation as validation } from 'constants/constant';
import { getTripBuilderExpireDateBounds, parseTripBuilderExpireDate, TripBuilderAdditionalInfoValues } from 'utils/shared/tripBuilder';
import ScreenshotSupport from '../../ScreenshotSupport';
import { getErrorInField, validate } from 'utils/validation';
import { dateFormat } from 'utils/shared/utils';
import TripBuilderHotelImage from './TripBuilderHotelImage';

export type { TripBuilderAdditionalInfoValues };

export type TripBuilderAdditionalInfoProposalSettings = {
  showCancellationDate: boolean;
  displayTotalDelta: boolean;
  cancellationDateDelta: string;
  proposalImage: string;
};

const defaultProposalSettings = (): TripBuilderAdditionalInfoProposalSettings => ({
  showCancellationDate: true,
  displayTotalDelta: false,
  cancellationDateDelta: String(DEFAULT_CANCELLATION_DATE_DELTA),
  proposalImage: '',
});

const mergeProposalSettings = (
  settings?: Partial<TripBuilderAdditionalInfoProposalSettings>,
): TripBuilderAdditionalInfoProposalSettings => ({
  ...defaultProposalSettings(),
  ...settings,
});

type TripBuilderAdditionalInfoField = Exclude<
  keyof TripBuilderAdditionalInfoValues,
  'expireDate' | 'lastName'
>;

const FIELDS: TripBuilderAdditionalInfoField[] = ['firstName', 'email', 'password'];

const FIELD_CONFIG: Record<
  TripBuilderAdditionalInfoField,
  { label?: string; labelKey?: string; type?: string }
> = {
  firstName: { labelKey: 'name' },
  email: { labelKey: 'passenger.email' },
  password: { labelKey: 'login.password', type: 'password' },
};

interface TripBuilderAdditionalInfoModalProps {
  close: () => void;
  values: TripBuilderAdditionalInfoValues;
  onSave?: (values: TripBuilderAdditionalInfoValues) => void;
  showValidation?: boolean;
  proposalSettings?: Partial<TripBuilderAdditionalInfoProposalSettings>;
  onProposalSettingsSave?: (settings: TripBuilderAdditionalInfoProposalSettings) => void;
}

const TripBuilderAdditionalInfoModal: FC<TripBuilderAdditionalInfoModalProps> = ({
  close,
  onSave,
  onProposalSettingsSave,
  values,
  showValidation = false,
  proposalSettings,
}) => {
  const { t } = useTranslation();
  const [draftValues, setDraftValues] = useState(values);
  const [draftProposalSettings, setDraftProposalSettings] = useState(() =>
    mergeProposalSettings(proposalSettings),
  );
  const [isSubmitted, setIsSubmitted] = useState(showValidation);
  const { minDate, maxDate } = useMemo(() => getTripBuilderExpireDateBounds(), []);
  const expireDateValue = useMemo(
    () => parseTripBuilderExpireDate(draftValues.expireDate),
    [draftValues.expireDate],
  );

  useEffect(() => {
    setDraftValues(values);
  }, [values]);

  useEffect(() => {
    setDraftProposalSettings(mergeProposalSettings(proposalSettings));
  }, [proposalSettings]);

  useEffect(() => {
    if (showValidation) {
      setIsSubmitted(true);
    }
  }, [showValidation]);

  const isExpireDateValid = () => {
    if (!draftValues.expireDate) {
      return false;
    }

    if (getErrorInField(validation.expireDate, draftValues.expireDate, true)) {
      return false;
    }

    const date = parseTripBuilderExpireDate(draftValues.expireDate);
    if (!date) {
      return false;
    }

    return date >= minDate && date <= maxDate;
  };

  const onSubmit = () => {
    setIsSubmitted(true);
    const isValid = validate(validation, draftValues);
    if (!isValid) {
      return;
    }
    onSave?.(draftValues);
    if (onProposalSettingsSave) {
      onProposalSettingsSave(draftProposalSettings);
    }
    close();
  };

  const isExpireDateSubmitted = isSubmitted && !isExpireDateValid();

  return (
    <Fragment>
      {createPortal(
        <div className="modal-overlay modal-overlay-top">
          <div className="modal modal-sm active tb-additional-info-modal">
            <div className="modal-title">
              <div className="title">Additional info</div>
              <div>
                <ScreenshotSupport />
                <FontAwesomeIcon icon={faTimes} onClick={close} />
              </div>
            </div>
            <div className="modal-content">
              <FormGroupDatepicker
                label="Expire Date"
                appendToBody
                value={expireDateValue}
                minDate={minDate}
                maxDate={maxDate}
                validation={validation.expireDate}
                isSubmitted={isExpireDateSubmitted}
                onChange={(date) =>
                  setDraftValues({
                    ...draftValues,
                    expireDate: dateFormat(date, 'yyyy-MM-dd'),
                  })
                }
              />
              {FIELDS.map((field) => {
                const config = FIELD_CONFIG[field];

                return (
                  <FormGroup
                    key={field}
                    name={field}
                    label={config.label ?? (config.labelKey ? t(config.labelKey) : '')}
                    value={draftValues[field]}
                    onChange={(value) => setDraftValues({ ...draftValues, [field]: value })}
                    validation={validation[field]}
                    isSubmitted={isSubmitted}
                    type={config.type}
                  />
                );
              })}
              <div className="pr-tb-settings flex-column">
                <div className="position-relative mb-10" style={{ width: '100%' }}>
                  <div className="text-sm mb-5">{t('proposal.cancellation_date_delta')}</div>
                  <SelectCustom
                    options={[0, 1, 2, 3, 4, 5].map((n) => ({
                      label: String(n),
                      value: String(n),
                    }))}
                    value={draftProposalSettings.cancellationDateDelta}
                    onChange={(value) =>
                      setDraftProposalSettings((prev) => ({
                        ...prev,
                        cancellationDateDelta: value,
                      }))
                    }
                  />
                </div>
                <div style={{ width: 220 }}>
                  <TripBuilderHotelImage
                    city="Main"
                    className="pr-tb-main-image"
                    imageUrl={draftProposalSettings.proposalImage || undefined}
                    uploadLabel="Upload Proposal Image"
                    onImageUrlChange={(url) =>
                      setDraftProposalSettings((prev) => ({
                        ...prev,
                        proposalImage: url || '',
                      }))
                    }
                  />
                </div>
                <Checkbox
                  checked={draftProposalSettings.showCancellationDate}
                  label={t('proposal.show_cancellation_date')}
                  className={
                    'mb-10 mt-10 ' + (draftProposalSettings.showCancellationDate ? 'text-bold' : '')
                  }
                  onChange={(value) =>
                    setDraftProposalSettings((prev) => ({
                      ...prev,
                      showCancellationDate: value,
                    }))
                  }
                />
                <Checkbox
                  checked={draftProposalSettings.displayTotalDelta}
                  label="Display total"
                  className={'mb-10 ' + (draftProposalSettings.displayTotalDelta ? 'text-bold' : '')}
                  onChange={(value) =>
                    setDraftProposalSettings((prev) => ({
                      ...prev,
                      displayTotalDelta: value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <Button className="btn btn-default" title={t('close')} onClick={close} />
              <Button className="btn btn-primary" title={t('save')} onClick={onSubmit} />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </Fragment>
  );
};

export default TripBuilderAdditionalInfoModal;
